import { type ReactNode } from 'react';
import { ThemeProvider, CssBaseline, Box, Typography, Button, Container } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { I18nextProvider, useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { theme } from './theme';
import i18n from '../shared/i18n';
import { AuthProvider } from '../shared/auth/AuthContext';
import { Snackbar } from '../shared/components/Snackbar';

dayjs.locale('pt-br');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: { retry: 0 },
  },
});

function ErrorFallback({ resetErrorBoundary }: FallbackProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('errors.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('errors.description')}
      </Typography>
      <Button variant="contained" onClick={resetErrorBoundary}>
        {t('errors.retry')}
      </Button>
    </Container>
  );
}

interface AppProvidersProps {
  children: ReactNode;
}

export function AppProviders({ children }: AppProvidersProps): JSX.Element {
  return (
    <I18nextProvider i18n={i18n}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
                  {children}
                </Box>
                <Snackbar />
              </ErrorBoundary>
            </AuthProvider>
          </QueryClientProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </I18nextProvider>
  );
}
